import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGS, getLangFromPath, stripLangPrefix, prefixPath } from "../seo/i18n";

/**
 * Polished, accessible language switcher used in headers across the public site.
 */
export default function LanguageSwitcher({ compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const switchTo = (code) => {
    const pathWithoutLang = stripLangPrefix(location.pathname);
    const newPath = prefixPath(pathWithoutLang, code);
    navigate(newPath);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors ${
          compact
            ? "px-2 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            : "px-3 py-2 text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
        }`}
        data-testid="lang-switcher-trigger"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Globe className="w-3.5 h-3.5" />
        <span>{current.native}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50"
          data-testid="lang-switcher-menu"
        >
          {LANGS.map((l) => (
            <button
              type="button"
              key={l.code}
              onClick={() => switchTo(l.code)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors ${
                l.code === current.code ? "bg-blue-50/40 text-blue-700 font-semibold" : "text-slate-700"
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium">{l.native}</span>
                <span className="text-[11px] text-slate-400">{l.label}</span>
              </span>
              {l.code === current.code && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
